import { differenceInCalendarDays, isValid, parseISO } from "date-fns";

const formatters = new Map<string, Intl.DateTimeFormat>();

const formatterOf = (locale: string, options: Intl.DateTimeFormatOptions) => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    formatters.set(key, formatter);
  }
  return formatter;
};

/**
 * "10:05 AM" today, "Yesterday", the weekday within a week, else "7 October".
 * The locale picks the clock, the names and the order.
 */
export function formatNotificationDate(
  isoDate: string,
  locale: string,
  yesterdayLabel: string,
  now: Date = new Date(),
): string {
  const date = parseISO(isoDate);
  if (!isValid(date)) return "";

  const daysDiff = differenceInCalendarDays(now, date);

  if (daysDiff <= 0) {
    return formatterOf(locale, { hour: "numeric", minute: "2-digit" }).format(
      date,
    );
  }

  if (daysDiff === 1) {
    return yesterdayLabel;
  }

  if (daysDiff <= 6) {
    return formatterOf(locale, { weekday: "long" }).format(date);
  }

  return formatterOf(locale, { day: "numeric", month: "long" }).format(date);
}

/**
 * Turns the absolute link from the server into an in-app path.
 * Returns null when the link is missing or unparsable.
 */
export function toAppPath(link: string | null | undefined): string | null {
  if (!link) return null;
  if (link.startsWith("/")) return link;

  try {
    const url = new URL(link);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}
